import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthUser } from '@/lib/auth/common/useSession';
import axiosHandler from '@/utils/axios-handler';
import { fetchLearningPathProgress, fetchLearningPathLessons, LearningPathLessonsResponse } from '@/lib/api/get-user-learning-paths';
import { CMSCourseDetails, LearningPathwayCourses, CMSLesson, CMSChapter, ChapterLessons } from '@/types/cms-lp';
import { CourseProgress, UserLearningPath } from '@/types/learning-paths';
import { computeLpAssessmentUnlocked } from '@/lib/utils/learning-path-utils';
import { academyApiUrl } from '@/lib/constants/api';

export interface UserLearningPathData {
  entryUid?: string;
  uid?: string;
  learningPathUid?: string;
  isSubscribed?: boolean;
  courses?: UserLearningPath['courses'];
  progress?: {
    hasStarted?: boolean;
    percentage?: number;
    isCompleted?: boolean;
    isLearningMaterialCompleted?: boolean;
  };
}

export interface LessonProgress {
  isCompleted: boolean;
  hasStarted: boolean;
  percentage: number;
  completedAt: string | null;
}

export interface CourseWithProgress extends CMSCourseDetails {
  userProgress?: CourseProgress;
  hasStarted: boolean;
  isCompleted: boolean;
}

export interface LessonWithProgress extends CMSLesson {
  userProgress?: LessonProgress;
  hasStarted: boolean;
  isCompleted: boolean;
}

export interface ChapterLessonsWithProgress extends Omit<ChapterLessons, 'lessons'> {
  lessons: LessonWithProgress[];
}

export interface ChapterWithProgress extends Omit<CMSChapter, 'lessons'> {
  lessons: ChapterLessonsWithProgress;
}

const CACHE_CONFIG = {
  staleTime: 0,
  gcTime: 5 * 60 * 1000, // 5 minutes
} as const;

export const learningPathKeys = {
  all: ['learningPath'] as const,
  detail: (uid: string) => [...learningPathKeys.all, 'detail', uid] as const,
  progress: (uid: string) => [...learningPathKeys.all, 'progress', uid] as const,
  lessonProgress: (uid: string) => [...learningPathKeys.all, 'lessonProgress', uid] as const,
} as const;

/**
 * The enrolled-learning-paths query, in one place.
 *
 * Both this file's useUserLearningPaths() (which derives a lookup map) and
 * ProductLearningPathsSection (which wants the raw list) need it. They previously
 * each declared their own copy against the SAME query key but with different
 * refetch options — which is how one cache entry ends up with inconsistent refetch
 * behaviour depending on which observer mounted first. Owning it here keeps the key,
 * the endpoint, the response unwrapping and the refetch policy in a single place.
 */
export function useEnrolledLearningPaths() {
  // Reactive to login/logout (userDataUpdated/userLoggedOut events via SessionHydrator).
  // The previous useState + mount-only useEffect only re-checked isUserLoggedIn() once,
  // so a login/logout that didn't remount this component (PartnerSessionSync's in-place
  // sync, or router.refresh() without a remount) left `enabled` stuck at the value from
  // first render — the enrolled-paths query never turned on after login, or kept
  // returning the previous user's data after logout.
  const { isLoggedIn } = useAuthUser();

  const { data: userLearningPaths } = useQuery<UserLearningPathData[]>({
    queryKey: ['user-learning-paths-enrolled'],
    queryFn: async () => {
      // No isUserLoggedIn() re-check here — `enabled: isLoggedIn` below already
      // guarantees this never runs while logged out.
      const response = await axiosHandler({
        method: 'GET',
        url: academyApiUrl('/v1/users/learning-paths?isSubscribed=true'),
        withCredentials: true,
      });

      if ('data' in response && response.status === 200) {
        const data = response.data?.userLearningPaths || response.data?.learningPaths || response.data || [];
        return Array.isArray(data) ? data : [];
      }
      return [];
    },
    enabled: isLoggedIn,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  return { userLearningPaths, isLoggedIn };
}

export function useUserLearningPaths() {
  const { userLearningPaths, isLoggedIn } = useEnrolledLearningPaths();

  const enrolledMap = useMemo(() => {
    const map = new Map<
      string,
      { isSubscribed: boolean; hasStarted: boolean; progressPercent?: number; isCompleted?: boolean; isLearningMaterialCompleted?: boolean }
    >();
    // isLoggedIn, not just data truthiness: `enabled` turning off on logout doesn't
    // clear the query's already-fetched data, so without this check the previous
    // session's enrolled/progress state would keep rendering after logout.
    if (isLoggedIn && userLearningPaths && Array.isArray(userLearningPaths)) {
      userLearningPaths.forEach((lp: UserLearningPathData) => {
        const uid = lp.entryUid || lp.uid || lp.learningPathUid;
        const isSubscribed = lp.isSubscribed ?? true;
        const hasStarted = lp.progress?.hasStarted ?? false;
        const progressPercent = lp.progress?.percentage;
        const isCompleted = lp.progress?.isCompleted;
        const lpAssessmentUnlocked = computeLpAssessmentUnlocked(lp as UserLearningPath);
        if (uid) {
          map.set(uid, { isSubscribed, hasStarted, progressPercent, isCompleted, isLearningMaterialCompleted: lpAssessmentUnlocked });
        }
      });
    }
    return map;
  }, [userLearningPaths, isLoggedIn]);

  return { enrolledMap, isLoggedIn };
}

/** Fetch courses with user progress merged into CMS data */
export function useLearningPathCourseProgress(
  uid: string | undefined,
  courses: LearningPathwayCourses | undefined
) {
  // See useEnrolledLearningPaths() above for why this must be reactive rather than a
  // one-off isUserLoggedIn() read.
  const { isLoggedIn: loggedIn } = useAuthUser();
  const cmsData = useMemo(() => courses?.course ?? [], [courses?.course]);

  const query = useQuery<UserLearningPath>({
    queryKey: learningPathKeys.progress(uid!),
    queryFn: () => fetchLearningPathProgress(uid!),
    enabled: loggedIn && !!uid,
    staleTime: CACHE_CONFIG.staleTime,
    gcTime: CACHE_CONFIG.gcTime,
    refetchOnMount: true,
    placeholderData: (prev) => prev,
  });

  const coursesWithProgress = useMemo((): CourseWithProgress[] => {
    // loggedIn, not just data truthiness — see useEnrolledLearningPaths() above.
    const userProgressData = loggedIn ? query.data : undefined;

    return cmsData.map((cmsCourse) => {
      const userCourse = userProgressData?.courses?.find(
        (uc) => uc.entryUid === cmsCourse.uid
      );

      return {
        ...cmsCourse,
        userProgress: userCourse?.progress,
        hasStarted: userCourse?.progress?.hasStarted ?? false,
        isCompleted: userCourse?.progress?.isCompleted ?? false,
      };
    });
  }, [cmsData, query.data, loggedIn]);

  return {
    ...query,
    data: coursesWithProgress,
    learningPathProgress: query.data,
  };
}

/** Fetch lessons with user progress merged into CMS chapters */
export function useLearningPathLessonProgress(
  courseUid: string | undefined,
  course: CourseWithProgress | undefined
) {
  // See useEnrolledLearningPaths() above for why this must be reactive rather than a
  // one-off isUserLoggedIn() read.
  const { isLoggedIn: loggedIn } = useAuthUser();
  const chapters = useMemo(() => course?.chapters ?? [], [course?.chapters]);

  const query = useQuery<LearningPathLessonsResponse>({
    queryKey: learningPathKeys.lessonProgress(courseUid!),
    queryFn: () => fetchLearningPathLessons(courseUid!),
    enabled: loggedIn && !!courseUid,
    staleTime: CACHE_CONFIG.staleTime,
    gcTime: CACHE_CONFIG.gcTime,
    refetchOnMount: true,
    placeholderData: (prev) => prev,
  });

  const chaptersWithProgress = useMemo((): ChapterWithProgress[] => {
    // loggedIn, not just data truthiness — see useEnrolledLearningPaths() above.
    const userProgressData = loggedIn ? query.data : undefined;

    return chapters.map((chapter) => {
      const lessonsWithProgress: LessonWithProgress[] = (chapter.lessons?.lessons ?? []).map((cmsLesson) => {
        const userLesson = userProgressData?.lessons?.find(
          (ul) => ul.entryUid === cmsLesson.uid
        );

        return {
          ...cmsLesson,
          userProgress: userLesson?.progress,
          hasStarted: userLesson?.progress?.hasStarted ?? false,
          isCompleted: userLesson?.progress?.isCompleted ?? false,
        };
      });

      return {
        ...chapter,
        lessons: {
          ...chapter.lessons,
          lessons: lessonsWithProgress,
        },
      };
    });
  }, [chapters, query.data, loggedIn]);

  return {
    ...query,
    data: chaptersWithProgress,
  };
}
